<!-- Session settings -->
<div
    #container
    class="w-full flex flex-row justify-between items-center gap-1 animate-height-snappy">
    @if (expandSettingsPanel) {
        <div class="flex flex-col justify-start items-center w-full opacity-0 animation-fade-in">
            <div class="flex flex-row justify-between items-center w-full">
                <div class="flex flex-row justify-start items-center gap-1">
                    <div class="text-[13px] text-color font-medium leadning-[normal] font-inter"
                        >Session Settings</div
                    >
                    <p-button
                        [buttonProps]="{ autofocus: false }"
                        (click)="onClickInfoCircle()"
                        size="small"
                        [outlined]="true"
                        class="fm-button-xsmall"
                        severity="secondary"
                        icon="pi pi-info-circle">
                        <span class="sr-only">Learn more about session settings</span>
                    </p-button>
                </div>
                <p-button
                    [buttonProps]="{ autofocus: false }"
                    size="large"
                    (click)="onClickToggleSessionSettingsPanel()"
                    severity="secondary"
                    [outlined]="true"
                    [icon]="
                        viewportService.isMobile() ? 'pi pi-angle-double-down' : 'pi pi-chevron-up'
                    "
                    styleClass="bp908:!w-6 bp908:!px-0">
                    <span class="sr-only">Close session settings panel</span>
                </p-button>
            </div>
            <label
                class="w-full text-left text-[12px] text-color font-medium leadning-[normal] font-inter mt-2"
                for="duration"
                >Duration</label
            >

            <div class="w-full flex items-center gap-1 mt-1">
                <p-select-button
                    class="w-full h-10"
                    styleClass="w-full"
                    [options]="durationOptions"
                    optionLabel="label"
                    [ngModel]="currentDuration?.value"
                    optionValue="value"
                    [allowEmpty]="false"
                    (onChange)="onSelectedDuration($event)">
                    <ng-template #item let-item>
                        <div class="flex flex-col justify-center items-center font-inter -gap-0.5">
                            <div class="font-semibold text-12 leading-[normal]">{{
                                item.label
                            }}</div>
                            <div class="font-medium text-11n">min</div>
                        </div>
                    </ng-template>
                </p-select-button>
            </div>

            <label
                class="w-full text-left text-[12px] text-color font-medium leadning-[normal] font-inter mt-2"
                for="activity"
                >My Task</label
            >
            <div class="w-full flex items-center gap-1 mt-1">
                <p-select-button
                    class="w-full h-10"
                    styleClass="w-full !px-0"
                    [options]="activityOptions"
                    optionLabel="label"
                    [ngModel]="currentActivity?.value"
                    optionValue="value"
                    [allowEmpty]="false"
                    (onChange)="onSelectedActivity($event)">
                    <ng-template #item let-item>
                        <div
                            class="flex flex-col justify-center items-center leading-snug font-medium">
                            <fa-icon class="text-[10px]" [icon]="item.icon" />
                            <div class="text-[12px]">{{ item.label }}</div>
                        </div>
                    </ng-template>
                </p-select-button>
            </div>

            <div class="flex flex-row justify-between items-center w-full mt-4">
                <label
                    id="quietModeLabel"
                    class="text-left text-[12px] text-color font-medium leading-[normal] font-inter"
                    for="quietMode"
                    >Quiet Mode</label
                >
                <p-toggleSwitch
                    class="flex"
                    (onChange)="onToggleQuietMode($event)"
                    [(ngModel)]="quietMode"
                    aria-labelledby="quietModeLabel" />
            </div>
            <fm-booking-hub-quiet-mode-match-hint [quietMode]="quietMode" />
            <div class="flex flex-row justify-between items-center w-full mt-4">
                <label
                    id="favoritesPreferenceModeLabel"
                    class="text-left text-[12px] text-color font-medium leading-[normal] font-inter"
                    for="quietMode"
                    >Prefer Favorites</label
                >
                <p-toggleSwitch
                    class="flex"
                    (onChange)="onTogglePreferFavorites($event)"
                    [ngModel]="preferFavorites()"
                    aria-labelledby="favoritesPreferenceModeLabel" />
            </div>
        </div>
    } @else {
        <div class="flex w-full gap-1 opacity-0 animation-fade-in">
            <div class="flex-1">
                <p-button
                    [buttonProps]="{ autofocus: false }"
                    [pTooltip]="currentDuration.tooltip"
                    tooltipPosition="top"
                    class="w-full flex"
                    size="large"
                    severity="secondary"
                    [outlined]="false"
                    styleClass="!px-0 w-full"
                    (click)="toggleDuration()">
                    <span class="sr-only">Toggle duration</span>
                    <div
                        class="w-full h-[30px] flex flex-col justify-center items-center text-13 relative overflow-hidden text-center font-inter font-semibold">
                        <div
                            class="absolute left-0 right-0 flex flex-col justify-center items-center"
                            [attr.aria-hidden]="animatingDurationLabel"
                            [ngClass]="{
                                'transition-all duration-200 ease-out': animatingDurationLabel,
                                'translate-y-0 opacity-100': !animatingDurationLabel,
                                '-translate-y-full opacity-0': animatingDurationLabel,
                            }">
                            {{ currentDuration?.label }}m
                        </div>

                        <div
                            class="absolute left-0 right-0 flex flex-col justify-center items-center"
                            [ngClass]="{
                                'transition-all duration-200 ease-out': animatingDurationLabel,
                                'translate-y-full opacity-0': !animatingDurationLabel,
                                'translate-y-0 opacity-100': animatingDurationLabel,
                            }"
                            [attr.aria-hidden]="!animatingDurationLabel">
                            {{ nextDuration?.label }}m
                        </div>
                    </div>
                </p-button>
            </div>

            <div class="flex-1">
                <p-button
                    [buttonProps]="{ autofocus: false }"
                    [pTooltip]="currentActivity.tooltip"
                    tooltipPosition="top"
                    class="w-full flex"
                    size="large"
                    severity="secondary"
                    [outlined]="false"
                    styleClass="!px-0 w-full"
                    (click)="toggleActivity()">
                    <span class="sr-only">Toggle activity type</span>
                    <div
                        class="w-full !h-[30px] relative overflow-hidden font-inter flex items-center justify-center">
                        <div
                            class="absolute left-0 right-0 flex flex-col items-center justify-center"
                            [ngClass]="{
                                'transition-all duration-200 ease-out': animatingActivityLabel,
                                'translate-y-0 opacity-100': !animatingActivityLabel,
                                '-translate-y-full opacity-0': animatingActivityLabel,
                            }"
                            [attr.aria-hidden]="animatingActivityLabel">
                            <fa-icon class="text-13 block" [icon]="currentActivity?.icon" />
                        </div>

                        <div
                            class="absolute left-0 right-0 flex flex-col items-center justify-center"
                            [ngClass]="{
                                'transition-all duration-200 ease-out': animatingActivityLabel,
                                'translate-y-full opacity-0': !animatingActivityLabel,
                                'translate-y-0 opacity-100': animatingActivityLabel,
                            }"
                            [attr.aria-hidden]="!animatingActivityLabel">
                            <fa-icon class="text-13 block" [icon]="nextActivity?.icon" />
                        </div>
                    </div>
                </p-button>
            </div>

            @if (quietMode) {
                <div class="flex-1">
                    <p-button
                        pTooltip="Quiet Mode"
                        tooltipPosition="top"
                        [buttonProps]="{ autofocus: false }"
                        class="w-full flex"
                        size="large"
                        severity="secondary"
                        [outlined]="false"
                        styleClass="!px-0 w-full"
                        (click)="disableQuietMode()">
                        <div
                            aria-hidden="true"
                            class="flex flex-col h-[30px] w-full justify-center items-center font-inter -gap-0.5 relative group">
                            <fa-icon
                                class="text-13 group-hover:opacity-20 transition-opacity"
                                [icon]="faMicrophoneSlash" />
                            <span
                                class="absolute inset-0 flex items-center justify-center pointer-events-none h-full w-full">
                                <i
                                    class="pi pi-times text-2xl text-color opacity-0 group-hover:opacity-100 transition-opacity"></i>
                            </span>
                        </div>
                        <span class="sr-only">Disable quiet mode</span>
                    </p-button>
                </div>
            }
            @if (preferFavorites()) {
                <div class="flex-1">
                    <p-button
                        [buttonProps]="{ autofocus: false }"
                        pTooltip="Prefer Favorites"
                        tooltipPosition="top"
                        class="w-full flex"
                        size="large"
                        severity="secondary"
                        [outlined]="false"
                        styleClass="!h-10 !px-0 w-full"
                        (click)="disableFavoritesPreference()">
                        <div
                            aria-hidden="true"
                            class="flex flex-col h-full w-full justify-center items-center font-inter -gap-0.5 relative group">
                            <fa-icon
                                class="text-13 group-hover:opacity-20 transition-opacity"
                                [icon]="faStar" />

                            <span
                                class="absolute inset-0 flex items-center justify-center pointer-events-none h-full w-full">
                                <i
                                    class="pi pi-times text-2xl text-color opacity-0 group-hover:opacity-100 transition-opacity"></i>
                            </span>
                        </div>
                        <span class="sr-only">Disable favorites preference</span>
                    </p-button>
                </div>
            }
        </div>
        <p-button
            [buttonProps]="{ autofocus: false }"
            size="large"
            (click)="onClickToggleSessionSettingsPanel()"
            severity="secondary"
            [outlined]="true"
            styleClass="bp908:!w-6 !p-0"
            [icon]="viewportService.isMobile() ? 'pi pi-angle-double-up' : 'pi pi-chevron-down'">
            <span class="sr-only">Open session settings panel</span>
        </p-button>
    }
</div>
